import { useState, useEffect, useCallback } from 'react';
import { BusinessClaimService } from '../domain/services/BusinessClaimService.js';
import { fetchCurrentUser } from '../domain/useCases/User/fetchCurrentUser.js';

/**
 * Custom hook for managing business claim functionality
 * Handles searching, claiming, and form management
 */
export const useClaimBusiness = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [userData, setUserData] = useState(null);
  const [claimingId, setClaimingId] = useState(null);
  const [claimSuccess, setClaimSuccess] = useState(null);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [formData, setFormData] = useState({
    claimReason: 'I am the authorized person of this entity and I have provided the required information and certificate. I would like to Claim this entity.',
    description: '',
    phoneNumber: '',
    emailAddress: '',
    businessRegistrationCertificate: null,
    panVatCertificate: null,
  });
  const [uploadProgress, setUploadProgress] = useState({});

  // Initialize user location on mount (optional)
  useEffect(() => {
    const initializeLocation = async () => {
      try {
        const location = await BusinessClaimService.getUserLocation();
        setUserLocation(location);
      } catch (err) {
        console.log('Location not available:', err.message);
      }
    };

    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'granted') {
          initializeLocation();
        }
      });
    }
  }, []);

  // Fetch user data on mount
  useEffect(() => {
    const initializeUserData = async () => {
      try {
        const data = await fetchCurrentUser();
        setUserData(data);
        setFormData((prev) => ({
          ...prev,
          phoneNumber: data?.contactNumber || data?.phoneNumber || data?.phone || '',
          emailAddress: data?.email || '',
        }));
      } catch (err) {
        console.log('Failed to fetch user data:', err);
      }
    };

    initializeUserData();
  }, []);

  /**
   * Search for businesses
   */
  const searchBusinesses = useCallback(
    async (query) => {
      if (!query.trim()) {
        setBusinesses([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await BusinessClaimService.searchBusinesses({
          query,
          location: userLocation,
        });
        setBusinesses(result.data || []);
      } catch (err) {
        setError(err.message);
        setBusinesses([]);
      } finally {
        setLoading(false);
      }
    },
    [userLocation]
  );

  /**
   * Upload document and return URL
   */
  const uploadDocument = useCallback(async (file, fieldName) => {
    try {
      setUploadProgress((prev) => ({ ...prev, [fieldName]: 'uploading' }));
      const url = await BusinessClaimService.uploadDocument(file);
      setUploadProgress((prev) => ({ ...prev, [fieldName]: 'done' }));
      return url;
    } catch (err) {
      setUploadProgress((prev) => ({ ...prev, [fieldName]: 'error' }));
      throw err;
    }
  }, []);

  /**
   * Submit business claim
   */
  const submitClaim = useCallback(
    async (businessId) => {
      setClaimingId(businessId);
      setError(null);

      try {
        // Validate claimReason is not empty
        if (!formData.claimReason || !formData.claimReason.trim()) {
          throw new Error('Claim reason cannot be empty');
        }

        setUploadingDocs(true);

        // Upload documents first
        let businessRegUrl = formData.businessRegistrationCertificate;
        let panVatUrl = formData.panVatCertificate;

        // If they're File objects, upload them
        if (formData.businessRegistrationCertificate instanceof File) {
          businessRegUrl = await uploadDocument(
            formData.businessRegistrationCertificate,
            'businessRegistrationCertificate'
          );
        }

        if (formData.panVatCertificate instanceof File) {
          panVatUrl = await uploadDocument(
            formData.panVatCertificate,
            'panVatCertificate'
          );
        }

        setUploadingDocs(false);

        // Submit claim with document URLs
        const claimPayload = {
          businessId,
          claimReason: formData.claimReason.trim(),
          description: formData.description.trim(),
          phoneNumber: formData.phoneNumber,
          emailAddress: formData.emailAddress,
          businessRegistrationCertificate: businessRegUrl,
          panVatCertificate: panVatUrl,
        };

        console.log('Submitting claim with payload:', claimPayload);

        await BusinessClaimService.submitClaim(claimPayload);

        setClaimSuccess(businessId);
        resetForm();
        setTimeout(() => setClaimSuccess(null), 3000);
      } catch (err) {
        setError(err.message);
      } finally {
        setClaimingId(null);
        setUploadingDocs(false);
      }
    },
    [formData, uploadDocument]
  );

  /**
   * Update form field
   */
  const updateFormField = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  /**
   * Update upload progress
   */
  const updateUploadProgress = useCallback((field, progress) => {
    setUploadProgress((prev) => ({
      ...prev,
      [field]: progress,
    }));
  }, []);

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setFormData((prev) => ({
      claimReason: 'I am the authorized person of this entity and I have provided the required information and certificate. I would like to Claim this entity.',
      description: '',
      phoneNumber: prev.phoneNumber,
      emailAddress: prev.emailAddress,
      businessRegistrationCertificate: null,
      panVatCertificate: null,
    }));
    setUploadProgress({});
  }, []);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    searchQuery,
    businesses,
    loading,
    error,
    userLocation,
    userData,
    claimingId,
    claimSuccess,
    formData,
    uploadProgress,
    uploadingDocs,

    // Setters
    setSearchQuery,
    setBusinesses,
    setError,

    // Methods
    searchBusinesses,
    submitClaim,
    updateFormField,
    updateUploadProgress,
    resetForm,
    clearError,
  };
};
